/**
 * Resolve human-readable order ids for profitability merged-cost rows.
 * Truth layer stores entity ULIDs in revenue_breakdown when display names are missing.
 */

export interface MergedCostOrderBreakdownItem {
  order_entity_id: string;
  order_name?: string;
  revenue: number;
  imputed?: boolean;
  attribution_tier?:
    | 'declared_same_day'
    | 'note_and_gps'
    | 'note_only'
    | 'planned'
    | 'unknown';
  leg_role?: 'collection' | 'trunk' | 'delivery' | 'unknown';
}

export interface MergedCostOrderBreakdown {
  orderIds: string[];
  orderRevenues: string[];
  orderRevenueImputed: string[];
  /** Aligned with orderIds — true when attributed as Same Day Tracker / TMS declared job. */
  orderSameDayFlags: boolean[];
  /** Orders where this vehicle is pick-up / trunk. */
  pickupOrderIds: string[];
  /** Orders where this vehicle is the delivery leg (excludes unknown / role-less). */
  deliveryOrderIds: string[];
  pickupCount: number;
  deliveryCount: number;
}

const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

export function isLikelyEntityUlid(value: string): boolean {
  return ULID_RE.test(value.trim());
}

function resolveDisplayOrderId(
  item: MergedCostOrderBreakdownItem,
  entityIdToOrderId?: Map<string, string>,
): string | null {
  const name = (item.order_name ?? '').trim();
  const entityId = (item.order_entity_id ?? '').trim();
  if (name && !isLikelyEntityUlid(name)) return name;

  const mapped = entityId ? entityIdToOrderId?.get(entityId)?.trim() : '';
  if (mapped) return mapped;

  if (name) return name;
  if (entityId && !isLikelyEntityUlid(entityId)) return entityId;
  return entityId || null;
}

function parseCsvField(raw?: string): string[] {
  if (!raw || typeof raw !== 'string') return [];
  return raw.split(',').map((value) => value.trim());
}

function isSameDayAttribution(item: MergedCostOrderBreakdownItem): boolean {
  return item.attribution_tier === 'declared_same_day';
}

/**
 * Tooltip / modal label for Daily Summary order IDs (plain text, e.g. title attrs).
 * Same-day TMS jobs often still display as entity ULIDs when TrackingID is absent.
 */
export const SAME_DAY_JOB_SUFFIX = '(same-day job)';

export function formatOrderIdForOrdersTooltip(orderId: string, _isSameDayJob = false): string {
  const id = (orderId ?? '').trim();
  if (!id) return id;
  if (isLikelyEntityUlid(id)) return `${id} ${SAME_DAY_JOB_SUFFIX}`;
  return id;
}

/** True when the display id should show the amber same-day suffix in UI. */
export function orderIdShowsSameDayLabel(orderId: string): boolean {
  return isLikelyEntityUlid((orderId ?? '').trim());
}

/** Per-order ids/revenues/imputed flags aligned by index. */
export function extractMergedCostOrderBreakdown(
  row: {
    order_ids?: string;
    order_revenues?: string;
    order_revenue_imputed?: string;
    revenue_breakdown?: MergedCostOrderBreakdownItem[];
    order_count?: number;
    pickup_count?: number;
    delivery_count?: number;
  },
  entityIdToOrderId?: Map<string, string>,
): MergedCostOrderBreakdown {
  const breakdown = row.revenue_breakdown;
  if (Array.isArray(breakdown) && breakdown.length > 0) {
    const orderIds: string[] = [];
    const orderRevenues: string[] = [];
    const orderRevenueImputed: string[] = [];
    const orderSameDayFlags: boolean[] = [];
    const pickupOrderIds: string[] = [];
    const deliveryOrderIds: string[] = [];
    const seen = new Set<string>();

    for (const item of breakdown) {
      const displayId = resolveDisplayOrderId(item, entityIdToOrderId);
      if (!displayId || seen.has(displayId)) continue;
      seen.add(displayId);
      orderIds.push(displayId);
      orderRevenues.push(String(item.revenue ?? ''));
      orderRevenueImputed.push(item.imputed ? '1' : '0');
      orderSameDayFlags.push(isSameDayAttribution(item) || isLikelyEntityUlid(displayId));
      const role = (item.leg_role || '').toLowerCase();
      if (role === 'collection' || role === 'trunk') pickupOrderIds.push(displayId);
      else if (role === 'delivery') deliveryOrderIds.push(displayId);
      // unknown / missing role → neither list (map must not invent a customer Target)
    }

    if (orderIds.length > 0) {
      // Prefer deduped ID list lengths so displayed counts match right-click modals.
      // Fall back to backend counters when role-less rows leave a list empty.
      const pickupCount =
        pickupOrderIds.length > 0
          ? pickupOrderIds.length
          : Number(row.pickup_count) || 0;
      const deliveryCount =
        deliveryOrderIds.length > 0
          ? deliveryOrderIds.length
          : row.delivery_count != null && Number.isFinite(Number(row.delivery_count))
            ? Number(row.delivery_count)
            : Math.max(0, (Number(row.order_count) || orderIds.length) - pickupCount);
      return {
        orderIds,
        orderRevenues,
        orderRevenueImputed,
        orderSameDayFlags,
        pickupOrderIds,
        deliveryOrderIds,
        pickupCount,
        deliveryCount,
      };
    }
  }

  const orderIds = parseCsvField(row.order_ids);
  const orderRevenues = parseCsvField(row.order_revenues);
  const orderRevenueImputed = parseCsvField(row.order_revenue_imputed);
  const pickupCount = Number(row.pickup_count) || 0;
  const deliveryCount =
    row.delivery_count != null && Number.isFinite(Number(row.delivery_count))
      ? Number(row.delivery_count)
      : Math.max(0, (Number(row.order_count) || orderIds.length) - pickupCount);

  return {
    orderIds,
    orderRevenues:
      orderRevenues.length > 0 ? orderRevenues : orderIds.map(() => ''),
    orderRevenueImputed:
      orderRevenueImputed.length > 0 ? orderRevenueImputed : orderIds.map(() => '0'),
    orderSameDayFlags: orderIds.map((id) => isLikelyEntityUlid(id)),
    // Legacy CSV has no per-order leg_role — IDs live under deliveries; pickups are count-only.
    pickupOrderIds: [],
    deliveryOrderIds: orderIds,
    pickupCount,
    deliveryCount: deliveryCount || orderIds.length,
  };
}

export function extractOrderIdsFromMergedCostRow(
  row: Parameters<typeof extractMergedCostOrderBreakdown>[0],
  entityIdToOrderId?: Map<string, string>,
): string[] {
  return extractMergedCostOrderBreakdown(row, entityIdToOrderId).orderIds;
}

/** Human-facing lookup ids (TrackingID / con_no / Qargo name) — excludes entity ULIDs. */
export function extractLookupOrderIdsFromMergedCostRow(
  row: Parameters<typeof extractMergedCostOrderBreakdown>[0],
  entityIdToOrderId?: Map<string, string>,
): string[] {
  return extractOrderIdsFromMergedCostRow(row, entityIdToOrderId).filter(
    (id) => id.trim().length >= 4 && !isLikelyEntityUlid(id),
  );
}

/** True when a record orderId matches any selected Lookup token (exact / case-insensitive / contains). */
export function orderIdMatchesLookupSelection(
  orderId: string,
  selectedOrderIds: string[],
): boolean {
  if (selectedOrderIds.length === 0) return true;
  const oid = (orderId ?? '').trim();
  if (!oid) return false;
  const oidLower = oid.toLowerCase();
  return selectedOrderIds.some((sel) => {
    const s = sel.trim();
    if (!s) return false;
    const sLower = s.toLowerCase();
    return oid === s || oidLower === sLower || oid.includes(s) || oidLower.includes(sLower);
  });
}
